import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { jwtDecode } from "jwt-decode";
import Cookies from "universal-cookie";
import queryString from "query-string";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Stack from "@mui/material/Stack";
import { get, post } from "../services/api";
import {
  EmployeeErrorState,
  EmployeeLoadingState,
  EmployeePageHeader,
  EmployeeSurface,
} from "../employee/components/EmployeePage";
import {
  AcknowledgementCard,
  calculateScore,
  CommentList,
  QuestionResult,
  ReportSection,
  ScoreOverview,
} from "../employee/components/PerformanceReport";

const groupByHeadingAndType = (data: any[]) =>
  data.reduce((groups: Record<string, any[]>, item) => {
    const key = `${item.surveyquestionheadingname}_${item.surveyquestiontypename}`;
    groups[key] = groups[key] ? [...groups[key], item] : [item];
    return groups;
  }, {});

const sectionId = (key: string) => `report-${key.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`;

const EmployeeSurveyResult: React.FC = () => {
  const { employeeId, surveyId } = useParams();
  const parsedToken = queryString.parse(window.location.search).access_token;
  const publicToken = typeof parsedToken === "string" ? parsedToken : "";

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [employeeDetails, setEmployeeDetails] = useState<any>({});
  const [questionList, setQuestionList] = useState<any[]>([]);
  const [reviewPeriod, setReviewPeriod] = useState<any[]>([]);
  const [surveyAck, setSurveyAck] = useState<{ date: string | null; comment: string }>({ date: null, comment: "" });

  const managerView = useMemo(() => {
    if (!publicToken) return false;
    try {
      return (jwtDecode(publicToken) as any).linkType === "viewSurveyResultsManager";
    } catch (_error) {
      return false;
    }
  }, [publicToken]);

  useEffect(() => {
    if (publicToken) localStorage.setItem("publicToken", publicToken);
    const cookieName = process.env.REACT_APP_PUBLIC_COOKIE_NAME || "";
    if (cookieName && publicToken) {
      new Cookies().set(cookieName, publicToken, { domain: ".paperlessnow.ca", path: "/surveyResults" });
    }
  }, [publicToken]);

  useEffect(() => {
    if (!employeeId || !surveyId || !publicToken) {
      setError("This performance-report link is incomplete or has expired. Please contact your administrator for a fresh link.");
      setLoading(false);
      return;
    }

    setLoading(true);
    get(`survey/getServices?type=getSurveyResultAck&surveyId=${surveyId}&access_token=${publicToken}`)
      .then((response: any) => {
        const acknowledgement = response?.payload?.data?.[0] || {};
        setSurveyAck({
          date: acknowledgement.ack_date || null,
          comment: acknowledgement.ack_comments || "No comments provided",
        });
      })
      .catch(() => undefined);

    Promise.all([
      get(`openEmployee/${employeeId}`, publicToken),
      get(`survey/getServices?type=getSurveyQuestions&employeeId=${employeeId}&surveyId=${surveyId}&access_token=${publicToken}`),
    ])
      .then(([employeeResponse, reportResponse]: any[]) => {
        setEmployeeDetails(employeeResponse?.payload?.data || {});
        setQuestionList(reportResponse?.payload?.data || []);
        setReviewPeriod(reportResponse?.payload?.reviewPeriod || []);
      })
      .catch(() => setError("We could not open this performance report. The link may have expired; please contact your administrator."))
      .finally(() => setLoading(false));
  }, [employeeId, publicToken, surveyId]);

  const groupedData = useMemo(() => groupByHeadingAndType(questionList), [questionList]);
  const detailGroups = Object.entries(groupedData).filter(([key]) => !key.endsWith("_Comment"));
  const scoredGroups = detailGroups.filter(([, items]) => calculateScore(items).result !== 0);
  const commentGroups = Object.entries(groupedData).filter(([key]) => key.endsWith("_Comment"));
  const employeeName = [employeeDetails.first_name, employeeDetails.last_name].filter(Boolean).join(" ");

  if (loading) return <EmployeeLoadingState message="Loading your performance report…" />;
  if (error) return <EmployeeErrorState message={error} />;

  return (
    <Stack spacing={3}>
      <EmployeePageHeader
        eyebrow="Performance report"
        title={employeeName || "Employee performance report"}
        description="How your management team rated you last quarter."
        action={reviewPeriod[0]?.review_period ? <Chip label={reviewPeriod[0].review_period} color="primary" variant="outlined" /> : undefined}
      />

      <ScoreOverview
        overall={calculateScore(questionList)}
        rows={scoredGroups.map(([key, items]) => ({
          key,
          label: key.split("_")[0],
          score: calculateScore(items),
        }))}
      />

      {(detailGroups.length > 1 || commentGroups.length > 0) && (
        <EmployeeSurface component="nav" aria-label="Report sections">
          <Stack direction="row" spacing={1} sx={{ px: 2, py: 1.5, overflowX: "auto" }}>
            {detailGroups.map(([key]) => (
              <Button key={key} href={`#${sectionId(key)}`} size="small" variant="text" sx={{ whiteSpace: "nowrap" }}>
                {key.split("_")[0]}
              </Button>
            ))}
            {commentGroups.length > 0 && <Button href="#report-comments" size="small" variant="text">Comments</Button>}
          </Stack>
        </EmployeeSurface>
      )}

      {detailGroups.map(([key, items]) => (
        <ReportSection
          key={key}
          id={sectionId(key)}
          title={key.split("_")[0]}
          description={`${calculateScore(items).result} of ${calculateScore(items).total} points`}
        >
          {items.map((item: any, index: number) => (
            <QuestionResult key={`${item.surveyquestionid}-${index}`} item={item} />
          ))}
        </ReportSection>
      ))}

      {commentGroups.length > 0 && (
        <ReportSection id="report-comments" title="Management comments" description="Written notes supplied with your ratings, if provided.">
          {commentGroups.map(([key, items]) => (
            <Box key={key} sx={{ "& + &": { mt: 2.5 } }}>
              <CommentList comments={items.map((item: any) => item.AnswerText)} />
            </Box>
          ))}
        </ReportSection>
      )}

      <AcknowledgementCard
        date={surveyAck.date}
        comment={surveyAck.comment}
        managerView={managerView}
        onSubmit={(comments) =>
          post("survey/postServices", {
            type: "saveSurveyResultsAck",
            comments: comments || null,
            surveyId,
          })
        }
      />
    </Stack>
  );
};

export default EmployeeSurveyResult;
